/**
 * Watchlist baseline-mode helpers.
 *
 * The watchlist has a global toggle ("avg_cost" | "current") that swaps the
 * baseline used to compute simulated returns. Avg-cost mode compares the
 * simulated premium to the user's actual purchase price; current mode keeps
 * the legacy behavior of comparing against today's market premium.
 *
 * Switching baselines is a pure render-time recomputation — the simulated
 * premiums themselves don't change.
 */

export const BASELINE_MODE_STORAGE_KEY = 'watchlist_baseline_mode';
export const BASELINE_MODES = { AVG_COST: 'avg_cost', CURRENT: 'current' };
export const DEFAULT_BASELINE_MODE = BASELINE_MODES.AVG_COST;

/**
 * Display label for the starting-price column (sticky "Current Premium").
 * In avg-cost mode the column rebrands to "Avg Cost" so the header matches
 * what the cell value is showing and what the simulated returns compare against.
 */
export function getStartingPriceColumnLabel(mode) {
  return mode === BASELINE_MODES.AVG_COST ? 'Avg Cost' : 'Current Premium';
}

/**
 * Two-line variant for narrow sticky cells (heatmap uses ~75px width).
 */
export function getStartingPriceColumnLabelShort(mode) {
  return mode === BASELINE_MODES.AVG_COST ? 'Avg\nCost' : 'Current\nPrem';
}

/**
 * Column-header tooltip text — matches the active baseline so hover state
 * doesn't contradict the cell value.
 */
export function getStartingPriceTooltip(mode) {
  return mode === BASELINE_MODES.AVG_COST
    ? "Your avg cost per contract — the baseline the simulated returns compare against. Toggle 'Compare to' in the filter row to compare against today's market premium instead."
    : "The current market price (premium) of the option contract per share.";
}

/**
 * Resolve the numeric baseline premium for a row given the toggle mode.
 * Falls back to the current premium when avg cost is missing or non-positive
 * (e.g., user didn't enter a cost basis), so the toggle is a no-op rather
 * than producing NaN/Infinity downstream.
 */
export function resolveBaseline(row, mode) {
  const current = parseFloat(row?.['Current Premium']);
  if (mode === BASELINE_MODES.AVG_COST) {
    const avg = parseFloat(row?.['Average Cost per Contract']);
    if (Number.isFinite(avg) && avg > 0) return avg;
  }
  return Number.isFinite(current) && current > 0 ? current : 0;
}

/** Annualization shared with `blackScholes.js` and the backend engine. */
function annualize(baseReturnPct, days) {
  if (!Number.isFinite(baseReturnPct) || !days || days <= 0) return null;
  try {
    return (Math.pow(1 + baseReturnPct / 100, 365 / days) - 1) * 100;
  } catch {
    return null;
  }
}

/**
 * Re-derive preset-mode simulated columns against the selected baseline.
 *
 * Inputs are read from the row exactly as the backend returns them; outputs
 * overwrite `Simulated Premium (+/-) % Change`, Buy `Simulated P&L (+/-)`,
 * Sell `Simulated P&L (+/-)`, and the annualized-return pair. `Simulated
 * Premium (+/-)` / `Simulated Equity (+/-)` / `Return on Collateral` are
 * unchanged because they don't depend on the baseline.
 *
 * When `mode === 'current'`, the row passes through untouched (no-op) so the
 * backend response is shown verbatim.
 */
export function applyBaselineToRow(row, mode) {
  if (!row || mode !== BASELINE_MODES.AVG_COST) return row;

  const baseline = resolveBaseline(row, mode);
  if (!(baseline > 0)) return row;

  const current = parseFloat(row['Current Premium']);
  if (!Number.isFinite(current) || current <= 0) return row;
  if (baseline === current) return row;

  const isSell = String(row['Position Type'] || '').toLowerCase() === 'sell';
  const numContracts = parseInt(row['Number of Contracts'], 10) || 1;
  const simUp = parseFloat(row['Simulated Premium (+)']);
  const simDown = parseFloat(row['Simulated Premium (-)']);

  // Days for annualization
  const daysTarget = parseInt(row['Days to Target'] ?? row['Days to Gain'], 10);
  const expDate = row['Expiration'] ? new Date(row['Expiration']) : null;
  let daysExpiry = null;
  if (expDate && !isNaN(expDate.getTime())) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expDate.setHours(0, 0, 0, 0);
    daysExpiry = Math.max(1, Math.ceil((expDate - today) / (1000 * 60 * 60 * 24)));
  }

  const next = { ...row };

  // % change uses baseline as denominator. For sells the sign flips
  // (profit when premium falls).
  const pctChange = (sim) => {
    if (!Number.isFinite(sim)) return null;
    if (isSell) return ((baseline - sim) / baseline) * 100;
    return ((sim - baseline) / baseline) * 100;
  };

  const pctUp = pctChange(simUp);
  const pctDown = pctChange(simDown);

  if (pctUp != null) next['Simulated Premium (+) % Change'] = Number(pctUp.toFixed(2));
  if (pctDown != null) next['Simulated Premium (-) % Change'] = Number(pctDown.toFixed(2));

  // Sell P&L uses the dollar delta between baseline and simulated premium
  // (per contract × 100 shares × num_contracts). Buy P&L is not produced by
  // the backend engine — leave it alone.
  if (isSell) {
    if (Number.isFinite(simUp)) {
      next['Simulated P&L (+)'] = Number(((baseline - simUp) * numContracts * 100).toFixed(2));
    }
    if (Number.isFinite(simDown)) {
      next['Simulated P&L (-)'] = Number(((baseline - simDown) * numContracts * 100).toFixed(2));
    }
  }

  // Annualized returns:
  // - Buy positions annualize the (re-baselined) premium % change
  // - Sell positions annualize Return on Collateral %, which is anchored to
  //   the premium actually received at open and not part of v1 scope —
  //   leave the backend-computed values untouched.
  if (!isSell) {
    if (pctUp != null) {
      const annTU = annualize(pctUp, daysTarget);
      const annEU = annualize(pctUp, daysExpiry);
      if (annTU != null) next['Ann. Return (Target) (+)'] = Number(annTU.toFixed(2));
      if (annEU != null) next['Ann. Return (Expiry) (+)'] = Number(annEU.toFixed(2));
    }
    if (pctDown != null) {
      const annTD = annualize(pctDown, daysTarget);
      const annED = annualize(pctDown, daysExpiry);
      if (annTD != null) next['Ann. Return (Target) (-)'] = Number(annTD.toFixed(2));
      if (annED != null) next['Ann. Return (Expiry) (-)'] = Number(annED.toFixed(2));
    }
  }

  return next;
}
